import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ProgramContainer } from '../../entities/formTemplateDataType';
import { ReactComponent as NotificatoinIcon } from "../../../public/assets/css/ontario-design-system/icons/svg/ontario-icon-notification.svg";
import { ReactComponent as UnreadMarker } from "../../../public/assets/css/ontario-design-system/icons/unread-marker.svg";
import { colour, Heading1, Heading2, Heading3, Heading4, spacing, typography } from '../../theme';
import colours from '../../theme/variables/colours';
import CustomPopup from './PopupComponent';
import { CXP_MAX_INBOX_MESSAGES } from '../../entities/Constants';
import { formatMessageDate } from '../../utilities/DateUtils';
import { getCxpMessages } from '../../services/CxpProfileService';
import { message } from '../../entities/CxpType';
import { cleanup, clearSessionStorage } from '../../security/PublicSecure';
import { isEventWithinElement } from '../../utilities/ElementUtils';

export const InboxButton = styled.button`
    border: none;
    cursor: pointer;
    background-color: #03713D;
    height: 48px;
    margin-right: ${spacing.spacing1};

    &: focus,
    &:active {
        box-shadow: 0 0 0 3px ${colour.focusBlue};
        transition: box-shadow 0.1s ease-in-out;
        border-radius: 0.25rem;
    }
   }
`;

export const UnreadCounter = styled.span`
    background-color: #D81A21;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    color: ${colours.palette.white};
    position: relative;
    right: ${spacing.spacing5};
    bottom: ${spacing.spacing2};
    height: 17px;
    width: 17px;
`;

export const InboxList = styled.ul`
    margin: ${spacing.spacing0};
    padding: ${spacing.spacing3} ${spacing.spacing0};

    background: ${colour.palette.white};
    border-radius: ${spacing.spacing1};
    box-shadow: 3px 4px 4px rgba(0, 0, 0, 0.25);
    list-style: none;
    & li:last-child {
        border-bottom: 1px solid ${colour.palette.greyscale20};
    }
`;

export const InboxHeadingContainer = styled.div`
    font-size: ${typography.defaultFontSize};
    padding-left: ${spacing.spacing5};
    width: 352px;
`;

export const StyledMessageLink = styled.a`
    text-decoration: none;
    color: ${colours.palette.black};
    &: hover, &:visited {
        color: ${colour.palette.black};
    }
`;

export const InboxListItem = styled.li<{ isRead?: boolean }>`
    border-top: 1px solid ${colour.palette.greyscale20};
    padding: ${spacing.spacing4} calc(${spacing.spacing5} - 3px);
    gap: ${spacing.spacing7};
    max-width: 352px;
    &: hover {
        cursor: pointer;
        background: ${colour.palette.greyscale5};
    }
    &:: marker {
        color: ${colours.palette.systemFocusBlue};
        content: ${p => (p.isRead ? 'none' : 'unset')};
    }

    svg {
        margin: ${spacing.spacing3} ${spacing.spacing2} ${spacing.spacing0} ${spacing.spacing2};
        width: 24px;
        height: 24px;
    }
`;

export const InboxListItemContainer = styled.div<{ isRead: boolean }>`
    display: inline-flex;
    padding-left: ${p => (p.isRead ? `calc(${spacing.spacing5} - 2px)` : `${spacing.spacing0}`)};
`;


export const TextParagraph = styled.p`
    font-size: inherit;
    line-height: 1.6;
    text-rendering: optimizeLegibility;
    margin: ${spacing.spacing0} ${spacing.spacing0} ${spacing.spacing5} ${spacing.spacing0};

    & + ul {
        margin-top: -${spacing.spacing3};
    }

    & + ${Heading1}, & + ${Heading2}, & + ${Heading3}, & + ${Heading4} {
        margin-top: ${spacing.spacing6};
    }
`;

export const TextParagraphBold = styled(TextParagraph)`
    font-weight: ${typography.fontWeightBold};
`;

export const MessageContainer = styled.div<{ isRead: boolean }>`
    display: flex;
    flex-direction: column;
    padding: ${spacing.spacing0} ${spacing.spacing2} ${spacing.spacing0}
        ${p => (p.isRead ? spacing.spacing5 : spacing.spacing3)};
`;

export const MessageSubject = styled(TextParagraph)<{ isRead: boolean }>`
    font-size: calc(${typography.fontPrimary}px - 1px);
    display: inline;
    margin: ${spacing.spacing0};
    font-weight: ${p =>
        p.isRead ? `${typography.fontWeightRegular}` : `${typography.fontWeightBold}`};
`;

export const MessageBody = styled(TextParagraph)`
    font-size: ${typography.defaultFontSize}px;
    margin: ${spacing.spacing0};

    text-overflow: ellipsis;
    overflow: hidden;
    width: 254px;
    white-space: nowrap;
`;

export const MessageDate = styled.p`
    font-size: calc(${typography.fontSecondary} - 1px);
    margin: ${spacing.spacing2} ${spacing.spacing0} ${spacing.spacing0};
    color: ${colours.palette.greyscale60};
`;

interface InboxNotificationProps {
    programContainer: ProgramContainer;
    lang: 'en' | 'fr';
}

const InboxNotificationComponent = ({
    programContainer,
    lang,
}: InboxNotificationProps) => {
    const [inboxActive, setInboxActive] = useState(false);
    const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
    const [messages, setMessages] = useState<message[]|undefined>();

    const fetchCxpMessages = async () => {
        try {
            const response = await getCxpMessages(programContainer.Profile.token, { pageNumber: 1, pageSize: 20, search: '' });
            if (!response) {
                setMessages(undefined);
            } else {
                setMessages(response);
            }
        } catch (error) {
            console.error("Error fetching CXP profile:", error.response?.data || error.message);
        }
    }

    useEffect(() => {
        // if(programContainer.CategoryId.toLowerCase()!='otris') {
            if (programContainer.Profile?.messages?.length > 0) {
                const unread = programContainer.Profile.messages.filter(message => !message.isRead).length;
                setUnreadMessagesCount(unread);
                setMessages(programContainer.Profile.messages);
            } else {
                setMessages(undefined);
                setUnreadMessagesCount(0);
            }
        // }
    }, [programContainer.Profile?.messages]);

    const getMessageLinkElement = (e: MouseEvent, parent: HTMLElement): HTMLAnchorElement | null => {
        let element = e.target as HTMLElement;
        while (element && element !== parent) {
            if (element.tagName === 'A') {
                return element as HTMLAnchorElement;
            }
            element = element.parentElement;
        }
        return null;
    }

    const handleInboxClick = (e: MouseEvent) => {
        const popup = document.getElementById('customInboxPopup')!;
        if (!popup.contains(e.target as Node)) {
            setInboxActive(false);
            return;
        }
        const linkElement = getMessageLinkElement(e, popup);
        if (linkElement) {
            e.preventDefault();
            const link = linkElement.getAttribute('href');
            if (link) {
                cleanup(programContainer.CategoryId.toLowerCase(), programContainer);
                window.location.href = link;
            }
        }
        setInboxActive(false);
    }

    if (!programContainer.Profile?.lastName || !programContainer.Profile?.firstName) {
        return null;
    };

    return (
        <>
            <InboxButton
                onClick={() => {
                    setInboxActive(!inboxActive);
                }}
                aria-expanded={!!inboxActive}
                aria-controls="customPopup"
                aria-label="Inbox"
            >
                <NotificatoinIcon role="presentation" focusable="false" color={colours.palette.white} className="ontario-icon" />
            </InboxButton>
            {unreadMessagesCount !== 0 && (
                <UnreadCounter>{unreadMessagesCount}</UnreadCounter>
            )}
            {inboxActive && (
                <CustomPopup
                    id="customInboxPopup"
                    onClickOutside={() => setInboxActive(false) }
                    isVisible={inboxActive}
                    handleCustomEvent={handleInboxClick}
                >
                    <InboxList>
                        <InboxHeadingContainer>
                            <TextParagraphBold>{lang === 'en' ? 'Inbox' : 'Boîte de réception'}</TextParagraphBold>
                            {(messages?.length===0 || unreadMessagesCount===0) && (
                                <TextParagraph>
                                    {lang === 'en' ? "You have no new messages." : "Vous n'avez aucun nouveau message."}
                                </TextParagraph>
                            )}
                        </InboxHeadingContainer>
                        {unreadMessagesCount > 0 && messages?.sort((a, b) =>
                                Date.parse(b.messageDateTime) - Date.parse(a.messageDateTime)
                            )
                            .slice(0, CXP_MAX_INBOX_MESSAGES)
                            .map(message => (
                                <StyledMessageLink
                                    key={`${message.messageId}link`}
                                    href={`${process.env.PUBLIC_CXP_WEB_BASE_PATH}/auth/signin?redirectUri=${lang==='en'? '' : '/fr'}/notifications/message?messageId=${message.messageId}`}
                                >
                                    <InboxListItem
                                        isRead={message.isRead}
                                        key={`${message.messageId}messageli`}
                                    >
                                        <InboxListItemContainer
                                            isRead={message.isRead}
                                            key={`${message.messageId}liContainer`}
                                        >
                                            {!message.isRead && (<UnreadMarker key={`${message.messageId}unreadMarker`} />)}
                                            <MessageContainer isRead={message.isRead} key={`${message.messageId}messagecontainer`}>
                                                <MessageSubject isRead={message.isRead} key={`${message.messageId}messageSubject`}>
                                                    {message.subject}
                                                </MessageSubject>
                                                <MessageBody key={`${message.messageId}messageBody`} >
                                                    {new DOMParser().parseFromString(message.body, 'text/html').documentElement.textContent}
                                                </MessageBody>
                                                <MessageDate key={`${message.messageId}messageDate`}>
                                                    {formatMessageDate(message.messageDateTime)}
                                                </MessageDate>
                                            </MessageContainer>
                                        </InboxListItemContainer>
                                    </InboxListItem>
                                </StyledMessageLink>
                            ))
                        }
                        {messages?.length && unreadMessagesCount && (
                            <InboxListItem>
                                <a href={`${process.env.PUBLIC_CXP_WEB_BASE_PATH}/auto/signin?redirectUri=${lang==='en' ? '' : '/fr'}/notifications/inbox`}>
                                    {lang === 'en' ? 'View My Inbox' : 'Voir tous les messages'}
                                </a>
                            </InboxListItem>
                        )}
                    </InboxList>
                </CustomPopup>
            )}
        </>
    );
};

export default InboxNotificationComponent;
